import React, { useState } from 'react'
import { Card, CardBody, CardHeader } from '@/design-system/components/Card'
import { Button } from '@/design-system/components/Button'
import { Badge } from '@/design-system/components/Badge'
import { useAdminClients } from './hooks/useAdmin'
import type { AdminClient } from './hooks/useAdmin'

const INVOICE_VARIANTS: Record<AdminClient['invoice_status'], 'complete' | 'pending' | 'urgent'> = {
  paid: 'complete',
  pending: 'pending',
  overdue: 'urgent',
}

const INVOICE_LABELS: Record<AdminClient['invoice_status'], string> = {
  paid: 'Betald',
  pending: 'Väntar',
  overdue: 'Försenad',
}

const PLAN_VARIANTS: Record<string, 'premium' | 'active' | 'default'> = {
  Enterprise: 'premium',
  Pro: 'active',
  Starter: 'default',
}

function AddCreditsModal({ client, onConfirm, onClose }: {
  client: AdminClient
  onConfirm: (amount: number) => void
  onClose: () => void
}) {
  const [amount, setAmount] = useState('')

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-surface-raised border border-surface-border rounded-xl w-full max-w-sm shadow-[0_16px_48px_rgba(0,0,0,0.6)]">
        <div className="px-5 py-4 border-b border-surface-border">
          <h3 className="text-sm font-semibold text-text-primary">Lägg till credits</h3>
          <p className="text-xs text-text-muted mt-0.5">{client.organization_name}</p>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <p className="text-xs text-text-secondary mb-1">Nuvarande saldo</p>
            <p className="text-lg font-bold text-text-primary">
              {new Intl.NumberFormat('sv-SE').format(client.credits_remaining)} credits
            </p>
          </div>
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1">Antal credits att lägga till</label>
            <input
              type="number"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              placeholder="t.ex. 5000"
              className="w-full h-9 px-3 rounded-lg bg-surface-overlay border border-surface-border text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-1 focus:ring-brand-accent"
            />
          </div>
        </div>
        <div className="px-5 pb-5 flex gap-3 justify-end">
          <Button variant="ghost" size="sm" onClick={onClose}>Avbryt</Button>
          <Button
            variant="primary"
            size="sm"
            disabled={!amount || Number(amount) <= 0}
            onClick={() => onConfirm(Number(amount))}
          >
            Lägg till
          </Button>
        </div>
      </div>
    </div>
  )
}

export function ClientManagement() {
  const { data, isLoading } = useAdminClients()
  const [creditTarget, setCreditTarget] = useState<AdminClient | null>(null)

  const handleAddCredits = (amount: number) => {
    console.log('Add credits', creditTarget?.id, amount)
    setCreditTarget(null)
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-text-primary">Kunder — Optic Insights</h1>
        <p className="text-sm text-text-secondary mt-1">{data.length} aktiva kunder</p>
      </div>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-surface-border">
                {['Klient', 'Plan', 'Credits kvar', 'Aktiva uppdrag', 'Faktura', ''].map(h => (
                  <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-text-muted uppercase tracking-wider whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-border">
              {isLoading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 6 }).map((__, j) => (
                      <td key={j} className="px-5 py-3.5">
                        <div className="h-4 bg-surface-overlay rounded animate-pulse w-24" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : data.map(client => (
                <tr key={client.id} className="hover:bg-surface-overlay/50 transition-colors">
                  <td className="px-5 py-4">
                    <p className="font-medium text-text-primary">{client.organization_name}</p>
                    <p className="text-xs text-text-muted mt-0.5">{client.contact_person} · {client.contact_email}</p>
                  </td>
                  <td className="px-5 py-4">
                    <Badge variant={PLAN_VARIANTS[client.plan] ?? 'default'}>{client.plan}</Badge>
                  </td>
                  <td className="px-5 py-4">
                    <p className={`font-semibold tabular-nums ${client.credits_remaining === 0 ? 'text-brand-danger' : client.credits_remaining < 2000 ? 'text-brand-warning' : 'text-text-primary'}`}>
                      {new Intl.NumberFormat('sv-SE').format(client.credits_remaining)}
                    </p>
                    {client.credits_remaining === 0 && (
                      <p className="text-xs text-brand-danger mt-0.5">Slut på credits</p>
                    )}
                  </td>
                  <td className="px-5 py-4">
                    <span className="tabular-nums font-medium text-text-primary">{client.active_missions}</span>
                  </td>
                  <td className="px-5 py-4">
                    <Badge variant={INVOICE_VARIANTS[client.invoice_status]}>
                      {INVOICE_LABELS[client.invoice_status]}
                    </Badge>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-2 justify-end">
                      <Button variant="ghost" size="sm">Se uppdrag</Button>
                      <Button variant="secondary" size="sm" onClick={() => setCreditTarget(client)}>
                        + Credits
                      </Button>
                      <Button variant="ghost" size="sm">Byt plan</Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {creditTarget && (
        <AddCreditsModal
          client={creditTarget}
          onConfirm={handleAddCredits}
          onClose={() => setCreditTarget(null)}
        />
      )}
    </div>
  )
}
